// TSK-5253: loose item weight reduction.
//
// A loose (manual quantity) KG / Gram item weighed inside the configured range is billed on the
// weight less a percentage. The cart line's `weight` stays the billing (reduced) quantity, so
// price, tax, stock and sync need no change. Three informational fields are kept beside it:
//   grossWeight            - what was weighed
//   weightReduction        - grossWeight - weight
//   weightReductionPercent - the percentage used
// Everything is off unless the till sets posConfig.enableWeightReduction = "Y".

import usePOSConfigStore from "../stores/usePOSConfigStore";

export const WEIGHT_REDUCTION_META_KEY = "Weight Reduction";

const KG_NAMES = ["kilogram", "kilograms", "kg", "kgs", "kgm"];
const GRAM_NAMES = ["gram", "grams", "g", "gm", "gms", "grm"];

const DEFAULT_PERCENT = 2;
const DEFAULT_MIN_KG = 0.1;
const DEFAULT_MAX_KG = 3;
const RANGE_TOLERANCE = 1e-9;

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Half-up rounding on the decimal digits, so 1.2495 at 3 places gives 1.250 and
// 1.5 * 0.98 gives 1.47, not 1.4699999999999998.
export const roundWeight = (value, precision) => {
  const number = toNumber(value);
  if (number === null) return NaN;
  const shifted = Number(`${number}e${precision}`);
  if (!Number.isFinite(shifted)) return parseFloat(number.toFixed(precision));
  return Number(`${Math.round(shifted)}e-${precision}`);
};

export const getWeightReductionSettings = (posConfig) => {
  if (posConfig?.enableWeightReduction !== "Y") return null;
  const percent = toNumber(posConfig.weightReductionPercent) ?? DEFAULT_PERCENT;
  const minKg = toNumber(posConfig.weightReductionMinKg) ?? DEFAULT_MIN_KG;
  const maxKg = toNumber(posConfig.weightReductionMaxKg) ?? DEFAULT_MAX_KG;
  if (!(percent > 0) || !(percent < 100) || minKg < 0 || !(maxKg > 0) || minKg > maxKg) return null;
  return { percent, minKg, maxKg };
};

export const isWeightReductionEnabled = (posConfig) => getWeightReductionSettings(posConfig) !== null;

// 1 for a Kilogram UOM, 0.001 for a Gram UOM, null for anything else (litre, metre, piece).
export const getWeightUomFactor = (product) => {
  const uom = Array.isArray(product?.uomData) ? product.uomData[0] : null;
  const names = uom ? [uom.name, uom.ediCode] : [product?.uom_name];
  const normalised = names.filter((name) => typeof name === "string").map((name) => name.trim().toLowerCase());
  if (normalised.some((name) => KG_NAMES.includes(name))) return 1;
  if (normalised.some((name) => GRAM_NAMES.includes(name))) return 0.001;
  return null;
};

export const getWeightPrecision = (product) => {
  // A whole-number UOM (GPR "Grams": isdecimal N, stdprecision 2) is never billed a fraction.
  if (product?.uomData?.[0]?.decimal === "N") return 0;
  const candidates = [product?.uomData?.[0]?.stdprecision, product?.isQtyDesimal];
  for (const candidate of candidates) {
    const parsed = parseInt(candidate, 10);
    if (Number.isInteger(parsed) && parsed >= 0 && parsed <= 6) return parsed;
  }
  return 3;
};

// Loose item sold by weight on its own line. Custom Mix items, combo / companion parents and
// their children, add-on lines, gift cards, coupons and returns are never reduced.
export const isWeightReductionCandidate = (product) => {
  if (!product || !product.isManualQty) return false;
  if (product.isReturn || product.groupedItems || product.isGiftCard || product.iscoupon) return false;
  if (product.productSegment === "CB" || product.companion === "Y") return false;
  if (product.children?.length > 0 || product.childItems?.length > 0 || product.selectedAddons?.length > 0) return false;
  return true;
};

// The one calculation. Returns null when the weighing does not qualify.
export const calculateWeightReduction = (grossQty, product, posConfig) => {
  const settings = getWeightReductionSettings(posConfig);
  if (!settings) return null;
  const factor = getWeightUomFactor(product);
  if (factor === null) return null;
  const precision = getWeightPrecision(product);
  const grossWeight = roundWeight(grossQty, precision);
  if (!(grossWeight > 0)) return null;
  const grossKg = grossWeight * factor;
  if (grossKg < settings.minKg - RANGE_TOLERANCE || grossKg > settings.maxKg + RANGE_TOLERANCE) return null;
  const netWeight = roundWeight((grossWeight * (100 - settings.percent)) / 100, precision);
  const weightReduction = roundWeight(grossWeight - netWeight, precision);
  // At a coarse UOM precision the reduction can round away (0.15 at 2 places).
  if (!(netWeight > 0) || !(weightReduction > 0)) return null;
  return { grossWeight, weightReduction, weightReductionPercent: settings.percent, netWeight };
};

const sameWeight = (a, b, precision) => {
  const first = toNumber(a);
  const second = toNumber(b);
  if (first === null || second === null) return false;
  return Math.abs(first - second) < 0.5 * 10 ** -precision;
};

// The line's reduction details, only when they still agree with its billing weight
// (gross - reduction = weight). A line whose quantity was changed by code that does not
// know these fields reads as "no reduction" instead of printing a wrong gross weight.
export const getWeightReductionInfo = (line) => {
  const grossWeight = toNumber(line?.grossWeight);
  const weightReduction = toNumber(line?.weightReduction);
  const netWeight = toNumber(line?.weight ?? line?.qty);
  if (!(grossWeight > 0) || !(weightReduction > 0) || !(netWeight > 0)) return null;
  const precision = getWeightPrecision(line);
  if (!sameWeight(grossWeight - weightReduction, netWeight, precision)) return null;
  return { grossWeight, weightReduction, weightReductionPercent: toNumber(line.weightReductionPercent), netWeight, precision };
};

/**
 * Decides, before addProductToCart changes the cart, what this call bills.
 *
 * @returns null (bill `qty` as it is) or
 *          { billQty, grossWeight, weightReduction, weightReductionPercent, netWeight } or
 *          { billQty, unchanged: true } when a weight popup was confirmed without a new weight.
 */
export const planWeightReduction = ({ product, qty, totalQtyFlag, quantityChange, deleteProductInCart, weighedEntry, existingLine, posConfig, isReturnCart }) => {
  if (!getWeightReductionSettings(posConfig)) return null;
  if (deleteProductInCart || isReturnCart || !isWeightReductionCandidate(product)) return null;
  const incoming = toNumber(qty);
  if (!(incoming > 0)) return null;

  // An added quantity counts only when it came from the weight popup (scale or typed there):
  // a label barcode, a pre-set quantity or the default quantity of 1 is not a weighing. A total
  // quantity typed on a cart line counts on tills that use the weight popup.
  const isWeighing = totalQtyFlag ? weighedEntry === true || (quantityChange === true && posConfig?.showWeightPopup === "Y") : weighedEntry === true;
  if (!isWeighing) return null;

  if (totalQtyFlag && existingLine) {
    const precision = getWeightPrecision(existingLine);
    const info = getWeightReductionInfo(existingLine);
    // The popup opens with the line's gross weight; the same value back means no new weighing.
    if (weighedEntry === true && info && sameWeight(incoming, info.grossWeight, precision)) {
      return { billQty: info.netWeight, unchanged: true };
    }
    // A price change or Enter on the typed-quantity box re-sends the line's own weight.
    if (weighedEntry !== true && sameWeight(incoming, existingLine.weight, precision)) return null;
  }

  const result = calculateWeightReduction(incoming, product, posConfig);
  return result ? { ...result, billQty: result.netWeight } : null;
};

const clearWeightReduction = (line) => {
  delete line.grossWeight;
  delete line.weightReduction;
  delete line.weightReductionPercent;
};

/**
 * Writes the informational fields on the line addProductToCart just added or changed.
 *
 * @param line      the cart line after its weight was set
 * @param plan      planWeightReduction's result for this call
 * @param qty       the quantity this call brought in (before any reduction)
 * @param prevInfo  getWeightReductionInfo of the line before the call (null for a new line)
 * @param prevWeight the line's weight before the call
 */
export const stampWeightReduction = (line, { plan, totalQtyFlag, qty, prevInfo, prevWeight, isNewLine }) => {
  if (!line) return;
  const hasFields = line.grossWeight !== undefined || line.weightReduction !== undefined || line.weightReductionPercent !== undefined;
  if (!plan && !hasFields) return;
  if (plan?.unchanged) return;
  const precision = getWeightPrecision(line);

  if (plan) {
    if (totalQtyFlag || isNewLine) {
      line.grossWeight = plan.grossWeight;
      line.weightReduction = plan.weightReduction;
    } else {
      // Each weighing is reduced on its own and the line adds them up.
      line.grossWeight = roundWeight((prevInfo ? prevInfo.grossWeight : toNumber(prevWeight) || 0) + plan.grossWeight, precision);
      line.weightReduction = roundWeight((prevInfo ? prevInfo.weightReduction : 0) + plan.weightReduction, precision);
    }
    line.weightReductionPercent = plan.weightReductionPercent;
    return;
  }

  if (!isNewLine && !totalQtyFlag && prevInfo) {
    // An unreduced quantity merged into a reduced line: the gross weight grows by the same amount.
    line.grossWeight = roundWeight(prevInfo.grossWeight + (toNumber(qty) || 0), precision);
    line.weightReduction = prevInfo.weightReduction;
    return;
  }
  if (!isNewLine && totalQtyFlag && sameWeight(line.weight, prevWeight, precision)) return;
  clearWeightReduction(line);
};

const currentPosConfig = () => usePOSConfigStore.getState().posConfig;

// Marks a quantity confirmed in the weight popup as a weighing; read once by addProductToCart.
export const markWeighedEntry = (product, posConfig = currentPosConfig()) => {
  if (product && isWeightReductionEnabled(posConfig)) product.weighedQtyEntry = true;
  return product;
};

// The line a weight popup is opened with for a quantity edit: it shows the gross weight, so a
// confirm without a new weighing is recognised and the reduced weight is not reduced again.
export const weightPopupProductForLine = (line, posConfig = currentPosConfig()) => {
  if (!isWeightReductionEnabled(posConfig)) return line;
  const info = getWeightReductionInfo(line);
  if (!info) return line;
  return { ...line, weight: info.grossWeight, rawWeight: String(info.grossWeight) };
};

const formatWeight = (value, precision) => roundWeight(value, precision).toFixed(precision);

// { gross: "Gross Wt: 1.500 KG", less: "Less 2%: 0.030 KG", net: "Net Wt: 1.470 KG", text } or null.
export const getWeightReductionPrintParts = (line) => {
  const info = getWeightReductionInfo(line);
  if (!info) return null;
  const factor = getWeightUomFactor(line);
  const unit = factor === 0.001 ? "G" : "KG";
  const { precision, weightReductionPercent: percent } = info;
  // A line holding one reduced and one unreduced weighing has no single percentage to print.
  const percentHolds = percent > 0 && Math.abs((info.grossWeight * percent) / 100 - info.weightReduction) < 10 ** -precision;
  const gross = `Gross Wt: ${formatWeight(info.grossWeight, precision)} ${unit}`;
  const less = `Less${percentHolds ? ` ${percent}%` : ""}: ${formatWeight(info.weightReduction, precision)} ${unit}`;
  const net = `Net Wt: ${formatWeight(info.netWeight, precision)} ${unit}`;
  return { gross, less, net, text: `${gross} | ${less} | ${net}` };
};

const buildMetaValue = (info) => ({
  grossWeight: info.grossWeight,
  weightReduction: info.weightReduction,
  weightReductionPercent: info.weightReductionPercent,
});

// Order-line metaData entry ({ key, value }) or null.
export const buildWeightReductionLineMeta = (line) => {
  const info = getWeightReductionInfo(line);
  if (!info) return null;
  return { key: WEIGHT_REDUCTION_META_KEY, value: JSON.stringify(buildMetaValue(info)) };
};

// Order metaData entry listing every reduced line. Sales History reads lines without their own
// metaData, so reprint rebuilds the Gross / Less / Net line from this entry.
export const buildWeightReductionOrderMeta = (items) => {
  if (!Array.isArray(items)) return null;
  const entries = [];
  items.forEach((item, position) => {
    const info = getWeightReductionInfo(item);
    if (!info) return;
    entries.push({ line: (position + 1) * 10, productId: item.productId || item.mProductId || null, qty: info.netWeight, ...buildMetaValue(info) });
  });
  return entries.length > 0 ? { key: WEIGHT_REDUCTION_META_KEY, value: JSON.stringify(entries) } : null;
};

// Reads the order metaData entry back. Returns a function that gives the fields for a
// retrieved line ({ productId, qty, line }) or null; each entry is handed out once.
export const createWeightReductionMetaReader = (orderMetaData) => {
  let entries = [];
  const entry = Array.isArray(orderMetaData) ? orderMetaData.find((meta) => meta?.key === WEIGHT_REDUCTION_META_KEY) : null;
  if (entry?.value) {
    try {
      const parsed = JSON.parse(entry.value);
      if (Array.isArray(parsed)) entries = parsed.map((item) => ({ ...item, used: false }));
    } catch (error) {
      console.error("[TSK-5253] Weight Reduction order metaData could not be read:", error?.message);
    }
  }
  return ({ productId, qty, line }) => {
    if (entries.length === 0) return null;
    const matches = (candidate) =>
      !candidate.used &&
      candidate.productId === productId &&
      getWeightReductionInfo({ weight: qty, grossWeight: candidate.grossWeight, weightReduction: candidate.weightReduction, isQtyDesimal: 3 }) !== null;
    const found = entries.find((candidate) => candidate.line === line && matches(candidate)) || entries.find(matches);
    if (!found) return null;
    found.used = true;
    return { grossWeight: found.grossWeight, weightReduction: found.weightReduction, weightReductionPercent: found.weightReductionPercent ?? null };
  };
};
